import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './NewHero.module.css';

import s1 from '../images/בית של יוקרה וחומר 1.webp';
import s2 from '../images/קרווילות צימרים 1.webp';
import s3 from '../images/בית של יוקרה וחומר 3.webp';
import s4 from '../images/בית מדברי מודרני 4.webp';
import s5 from '../images/בית של טבע וחומר 1.webp';
import logo from '../images/לוגו שובל רוימי 1.webp';

const SLIDES = [s1, s2, s3, s4, s5];
const SLIDE_DURATION = 7000;
const TOTAL_SLIDES = SLIDES.length;

const HEADLINE_LINES = [
  ['הבית', 'שלכם'],
  ['מתחיל', 'בתכנון', 'נכון.'],
];

const MENU_ITEMS = [
  { num: '01', label: 'ראשי', href: '#hero' },
  { num: '02', label: 'אודות', href: '#about' },
  { num: '03', label: 'תהליך העבודה', href: '#process' },
  { num: '04', label: 'שירותים', href: '#services' },
  { num: '05', label: 'פרויקטים', href: '#projects' },
  { num: '06', label: 'שאלות נפוצות', href: '#faq' },
  { num: '07', label: 'יצירת קשר', href: '#contact' },
];

export default function NewHero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [prevSlide, setPrevSlide] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [wordsRevealed, setWordsRevealed] = useState([]);
  const [scrollY, setScrollY] = useState(0);

  const timerRef = useRef(null);
  const heroRef = useRef(null);
  const prefersReducedMotion = useRef(false);

  // Check reduced motion preference
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mq.matches;
  }, []);

  // Trigger load animations
  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Staggered word reveal
  useEffect(() => {
    if (!loaded) return;
    const allWords = HEADLINE_LINES.flat();
    allWords.forEach((_, i) => {
      setTimeout(() => {
        setWordsRevealed((prev) => [...prev, i]);
      }, 200 + i * 120);
    });
  }, [loaded]);

  // Parallax scroll
  useEffect(() => {
    if (prefersReducedMotion.current) return;
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-rotate slides
  const goToSlide = useCallback((next) => {
    setCurrentSlide((curr) => {
      setPrevSlide(curr);
      return next;
    });
    // Clear exiting slide after transition
    setTimeout(() => setPrevSlide(null), 1500);
  }, []);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      goToSlide(undefined); // will be replaced below
    }, SLIDE_DURATION);
  }, [goToSlide]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCurrentSlide((curr) => {
        setPrevSlide(curr);
        setTimeout(() => setPrevSlide(null), 1500);
        return (curr + 1) % TOTAL_SLIDES;
      });
    }, SLIDE_DURATION);
    return () => clearInterval(timerRef.current);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleProgressClick = (index) => {
    if (index === currentSlide) return;
    clearInterval(timerRef.current);
    setPrevSlide(currentSlide);
    setCurrentSlide(index);
    setTimeout(() => setPrevSlide(null), 1500);
    timerRef.current = setInterval(() => {
      setCurrentSlide((curr) => {
        setPrevSlide(curr);
        setTimeout(() => setPrevSlide(null), 1500);
        return (curr + 1) % TOTAL_SLIDES;
      });
    }, SLIDE_DURATION);
  };

  const handleMenuClick = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    setTimeout(() => {
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    }, 400);
  };

  // Flatten headline words for indexing
  let wordIndex = 0;

  const parallaxTitle = prefersReducedMotion.current ? 0 : scrollY * 0.3;
  const parallaxSlides = prefersReducedMotion.current ? 0 : scrollY * 0.15;

  return (
    <section className={styles.hero} ref={heroRef} id="hero">
      {/* === Slideshow === */}
      <div
        className={styles.slideshowWrap}
        style={{ transform: `translateY(${parallaxSlides}px)` }}
      >
        {SLIDES.map((src, i) => {
          let slideClass = styles.slide;
          if (i === currentSlide) slideClass += ` ${styles.active}`;
          else if (i === prevSlide) slideClass += ` ${styles.exiting}`;
          return (
            <div key={i} className={slideClass}>
              <img
                src={src}
                alt=""
                className={styles.slideImage}
                loading={i === 0 ? 'eager' : 'lazy'}
                draggable={false}
              />
            </div>
          );
        })}
      </div>

      {/* === Overlays === */}
      <div className={styles.gradientOverlay} />
      <div className={styles.vignette} />
      <div className={styles.grain} />

      {/* === Logo === */}
      <img
        src={logo}
        alt="SRA - סטודיו לאדריכלות ועיצוב פנים"
        className={`${styles.heroLogo} ${loaded ? styles.revealed : ''}`}
      />

      {/* === Hamburger === */}
      <button
        className={`${styles.hamburger} ${menuOpen ? styles.open : ''}`}
        onClick={() => setMenuOpen((prev) => !prev)}
        aria-label={menuOpen ? 'סגור תפריט' : 'פתח תפריט'}
        aria-expanded={menuOpen}
      >
        <span className={styles.hamburgerLine} />
        <span className={styles.hamburgerLine} />
        <span className={styles.hamburgerLine} />
      </button>

      {/* === Content === */}
      <div
        className={styles.content}
        style={{ transform: `translateY(${parallaxTitle}px)` }}
      >
        <div className={styles.headlineBlock}>
          {HEADLINE_LINES.map((lineWords, lineIdx) => (
            <span className={styles.headlineLine} key={lineIdx}>
              {lineWords.map((word, wIdx) => {
                const globalIdx = wordIndex++;
                return (
                  <React.Fragment key={globalIdx}>
                    <span
                      className={`${styles.headlineWord} ${
                        wordsRevealed.includes(globalIdx) ? styles.revealed : ''
                      }`}
                      style={{
                        transitionDelay: `${globalIdx * 0.12}s`,
                      }}
                    >
                      {word}
                    </span>
                    {wIdx < lineWords.length - 1 && (
                      <span className={styles.headlineSpace} />
                    )}
                  </React.Fragment>
                );
              })}
            </span>
          ))}
        </div>

        <p className={`${styles.subtitle} ${loaded ? styles.revealed : ''}`}>
          אדריכלות שחוסכת טעויות, שומרת על התקציב, ומגשימה את מה שדמיינתם - בלי הפתעות בדרך.
        </p>

        <div className={`${styles.ctaRow} ${loaded ? styles.revealed : ''}`}>
          <a href="tel:0544499737" className={styles.ctaPrimary}>
            לשיחת ייעוץ חינם
          </a>
          <a href="#projects" className={styles.ctaGhost}>
            הפרויקטים שלנו
          </a>
        </div>
      </div>

      {/* === Slide Counter === */}
      <div className={`${styles.slideCounter} ${loaded ? styles.revealed : ''}`}>
        <span className={styles.counterCurrent}>
          {String(currentSlide + 1).padStart(2, '0')}
        </span>
        <span className={styles.counterSep}>/</span>
        <span className={styles.counterTotal}>
          {String(TOTAL_SLIDES).padStart(2, '0')}
        </span>
      </div>

      {/* === Progress Bars === */}
      <div className={`${styles.progressWrap} ${loaded ? styles.revealed : ''}`}>
        {SLIDES.map((_, i) => {
          let fillClass = styles.progressFill;
          if (i === currentSlide) fillClass += ` ${styles.filling}`;
          else if (i < currentSlide || (currentSlide === 0 && prevSlide === TOTAL_SLIDES - 1 && i < TOTAL_SLIDES))  {
            // Already passed slides are filled
            if (i < currentSlide) fillClass += ` ${styles.filled}`;
          }
          return (
            <div
              key={i}
              className={styles.progressBar}
              onClick={() => handleProgressClick(i)}
              role="button"
              tabIndex={0}
              aria-label={`שקופית ${i + 1}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleProgressClick(i);
                }
              }}
            >
              <div
                className={fillClass}
                key={`fill-${currentSlide}-${i}`}
              />
            </div>
          );
        })}
      </div>

      {/* === Scroll Indicator === */}
      <div className={`${styles.scrollIndicator} ${loaded ? styles.revealed : ''}`}>
        <span className={styles.scrollText}>גללו למטה</span>
        <span className={styles.scrollDot} />
      </div>

      {/* === Fullscreen Menu === */}
      <div
        className={`${styles.menuOverlay} ${menuOpen ? styles.open : ''}`}
        aria-hidden={!menuOpen}
      >
        <div className={styles.menuInner}>
          <div className={styles.menuTop}>
            <img src={logo} alt="" className={styles.menuLogo} />
            <span className={styles.menuStudioName}>
              SRA - סטודיו לאדריכלות ועיצוב פנים
            </span>
          </div>

          <ul className={styles.menuItems}>
            {MENU_ITEMS.map((item) => (
              <li key={item.num} className={styles.menuItem}>
                <a
                  href={item.href}
                  className={styles.menuLink}
                  onClick={(e) => handleMenuClick(e, item.href)}
                >
                  <span className={styles.menuNumber}>{item.num}</span>
                  <span className={styles.menuLabel}>{item.label}</span>
                </a>
              </li>
            ))}
          </ul>

          <div className={styles.menuFooter}>
            <a
              href="tel:0544499737"
              className={styles.menuContactItem}
            >
              054-449-9737
            </a>
            <a
              href="mailto:Shovalr00@gmail.com"
              className={styles.menuContactItem}
            >
              Shovalr00@gmail.com
            </a>
            <a
              href="https://instagram.com/sra_studio"
              className={styles.menuContactItem}
              target="_blank"
              rel="noopener noreferrer"
            >
              Instagram
            </a>
            <span className={styles.menuContactItem}>
              באר שבע, ישראל
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
